/**
 * Admin Navigation Utilities
 * Centralized navigation logic for admin screens
 */

import { NavigationProp } from '@react-navigation/native';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';

// Path to screen name mapping
export const ADMIN_PATH_TO_SCREEN: Record<string, keyof AdminStackParamList> = {
  '/residents': 'ResidentList',
  '/events': 'Events',
  '/bulletins': 'Bulletins',
  '/messages': 'Messages',
  '/resources': 'Resources',
  '/todo-lists': 'ToDos',
  '/funding': 'Funding',
  '/organizations': 'Organizations',
  '/user-management': 'UserManagement',
  '/properties': 'Properties',
  '/feature-flags': 'FeatureFlags',
  '/tag-management': 'TagManagement',
  '/thread-moderation': 'ThreadModeration',
  '/report-review': 'ReportReview',
  '/surveys-management': 'SurveysManagement',
} as const;

// Screen metadata for display purposes
export const ADMIN_SCREEN_METADATA: Record<
  keyof AdminStackParamList,
  {
    title: string;
    subtitle: string;
    path: string;
  }
> = {
  ResidentList: {
    title: 'Residents',
    subtitle: 'Manage community residents',
    path: '/residents',
  },
  ResidentDetails: {
    title: 'Resident Details',
    subtitle: 'View resident information',
    path: '/resident-details',
  },
  TransactionHistory: {
    title: 'Transaction History',
    subtitle: 'View resident transaction history',
    path: '/transaction-history',
  },
  Events: {
    title: 'Events',
    subtitle: 'Manage community events',
    path: '/events',
  },
  CreateEvent: {
    title: 'Create Event',
    subtitle: 'Create a new event',
    path: '/events/create',
  },
  EditEvent: {
    title: 'Edit Event',
    subtitle: 'Edit event details',
    path: '/events/edit',
  },
  Bulletins: {
    title: 'Bulletins',
    subtitle: 'Manage community bulletins',
    path: '/bulletins',
  },
  CreateBulletin: {
    title: 'Create Bulletin',
    subtitle: 'Create a new bulletin',
    path: '/bulletins/create',
  },
  EditBulletin: {
    title: 'Edit Bulletin',
    subtitle: 'Edit bulletin draft',
    path: '/bulletins/edit',
  },
  Messages: {
    title: 'Messages',
    subtitle: 'Manage community messages',
    path: '/messages',
  },
  CreateMessage: {
    title: 'Create Message',
    subtitle: 'Create a new message',
    path: '/messages/create',
  },
  EditMessage: {
    title: 'Edit Message',
    subtitle: 'Edit conversation details',
    path: '/messages/edit',
  },
  Chat: {
    title: 'Chat',
    subtitle: 'Conversation',
    path: '/chat',
  },
  Resources: {
    title: 'Resources',
    subtitle: 'Manage community resources',
    path: '/resources',
  },
  CreateResource: {
    title: 'Create Resource',
    subtitle: 'Create a new resource',
    path: '/resources/create',
  },
  EditResource: {
    title: 'Edit Resource',
    subtitle: 'Edit resource details',
    path: '/resources/edit',
  },
  ToDos: {
    title: "To Do's",
    subtitle: 'Recurring to-do lists',
    path: '/todo-lists',
  },
  TodoListOccurrence: {
    title: 'To-do list',
    subtitle: 'Check off items',
    path: '/todo-lists/occurrence',
  },
  CreateTodoList: {
    title: 'Create to-do list',
    subtitle: 'Set up a recurring checklist',
    path: '/todo-lists/create',
  },
  EditTodoList: {
    title: 'Edit to-do list',
    subtitle: 'Edit the list definition',
    path: '/todo-lists/edit',
  },
  Funding: {
    title: 'Funding',
    subtitle: 'Manage funding and accounting',
    path: '/funding',
  },
  Organizations: {
    title: 'Organizations',
    subtitle: 'Manage organizations',
    path: '/organizations',
  },
  CreateOrganization: {
    title: 'Create Organization',
    subtitle: 'Create a new organization',
    path: '/organizations/create',
  },
  EditOrganization: {
    title: 'Edit Organization',
    subtitle: 'Edit organization details',
    path: '/organizations/edit',
  },
  UserManagement: {
    title: 'Admin User Management',
    subtitle: 'Manage admin users',
    path: '/user-management',
  },
  CreateUser: {
    title: 'Create User',
    subtitle: 'Create a new admin user',
    path: '/user-management/create',
  },
  EditUser: {
    title: 'Edit User',
    subtitle: 'Edit admin user details',
    path: '/user-management/edit',
  },
  Properties: {
    title: 'Properties',
    subtitle: 'Manage properties',
    path: '/properties',
  },
  CreateProperty: {
    title: 'Create Property',
    subtitle: 'Create a new property',
    path: '/properties/create',
  },
  EditProperty: {
    title: 'Property Details',
    subtitle: 'View property details',
    path: '/properties/edit',
  },
  FeatureFlags: {
    title: 'Feature Flags',
    subtitle: 'Manage feature flags',
    path: '/feature-flags',
  },
  TagManagement: {
    title: 'Newsfeed Tags',
    subtitle: 'Manage newsfeed tags',
    path: '/tag-management',
  },
  ThreadModeration: {
    title: 'Thread Moderation',
    subtitle: 'Moderate community threads',
    path: '/thread-moderation',
  },
  ReportReview: {
    title: 'Report Review',
    subtitle: 'Review reported community content',
    path: '/report-review',
  },
  SurveyResponses: {
    title: 'Survey Responses',
    subtitle: 'View survey results',
    path: '/survey-responses',
  },
  SurveysManagement: {
    title: 'Surveys',
    subtitle: 'Manage community surveys',
    path: '/surveys-management',
  },
} as const;

/**
 * Navigation handler factory
 * Creates a navigation handler function for admin screens
 */
export const createAdminNavigationHandler = (
  navigation?: NavigationProp<AdminStackParamList>,
) => {
  return (path: string) => {
    const screenName = ADMIN_PATH_TO_SCREEN[path];

    if (screenName && navigation) {
      // Handle screens that require parameters vs those that don't
      if (screenName === 'Chat') {
        // Chat screen requires conversationId parameter, but this function doesn't handle it
        // This path-based navigation shouldn't be used for Chat screen
        return;
      }
      if (screenName === 'ResidentDetails') {
        // ResidentDetails screen requires accountId and leaseId parameters
        // This path-based navigation shouldn't be used for ResidentDetails screen
        return;
      }
      (navigation as any).navigate(screenName);
    } else {
      console.log(
        `❌ Navigation failed - path: ${path}, screen: ${screenName}, hasNavigation: ${!!navigation}`,
      );
    }
  };
};

/**
 * Get screen metadata by screen name
 */
export const getScreenMetadata = (screenName: keyof AdminStackParamList) => {
  return ADMIN_SCREEN_METADATA[screenName];
};
